import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import PaginatedTable from '../PaginatedTable';
import CsvButton from '../CsvButton';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import { yearColor } from '../lib/colors';
import { fmtCount, fmtMonthNumber } from '../lib/format';

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Evidence for `demand_season`.
 *
 * `facts`: { items: [{ year, month, clicks, impressions, days, complete }],
 *            summary: { years, peak_month, complete_months, note } }
 *
 * One series per year from `summary.years`, X axis = month 1–12. A month with
 * `complete: false` is drawn dimmed and flagged in the caption — thirteen days of
 * August is not August, and overlaying it on a full August invites the reader to
 * conclude demand collapsed.
 *
 * `summary.note` is NOT rendered (§9-N2). Its content — "A month is only comparable
 * across years when days >= 28. Partial months are returned so the chart can dim them"
 * — reads exactly like `direction_note`: it is an instruction to whatever draws this
 * chart, not a caption for an owner. F6's forbidden list does not name it, so this is a
 * judgement rather than a rule; suppressing a caption is recoverable, leaking an
 * internal instruction onto a customer report is not. `SeasonOverlay.test.js` asserts
 * the suppression so flipping the decision is a one-line, visible change.
 */
/**
 * Columns for the expanded monthly table (spec v2 §7: "Monthly table with day counts").
 *
 * The day count is a column rather than a footnote here: in a table the reader is
 * scanning rows side by side, and "August: 404 visits" next to "July: 2,290" is only
 * honest if "11 days" is right beside it.
 */
const MONTH_COLUMNS = [
  { key: 'year', label: 'Year', render: (r) => r.year },
  { key: 'month', label: 'Month', render: (r) => fmtMonthNumber(r.month) },
  { key: 'impressions', label: 'Showed up in searches', align: 'right', render: (r) => fmtCount(r.impressions) },
  { key: 'clicks', label: 'Visits', align: 'right', render: (r) => fmtCount(r.clicks) },
  { key: 'days', label: 'Days covered', align: 'right', render: (r) => fmtCount(r.days) },
  { key: 'complete', label: 'Full month', render: (r) => (r.complete === false ? 'Partial' : 'Yes') },
];

/** Export columns — raw payload values, not the rendered strings (see `lib/csv.js`). The
 *  month ships as its number so a spreadsheet sorts it chronologically, not as "Apr" < "Jan". */
const CSV_COLUMNS = [
  { header: 'Year', get: (r) => r.year },
  { header: 'Month', get: (r) => r.month },
  { header: 'Showed up in searches', get: (r) => r.impressions },
  { header: 'Visits', get: (r) => r.clicks },
  { header: 'Days covered', get: (r) => r.days },
  { header: 'Full month', get: (r) => (r.complete === false ? 'Partial' : 'Yes') },
];

const SeasonOverlay = ({ facts, sentence, collapsed = true }) => {
  const items = Array.isArray(facts?.items) ? facts.items : [];
  const years = Array.isArray(facts?.summary?.years) ? facts.summary.years : [];

  // Pivot rows into one point per month with a column per year. This is a reshape of
  // the returned values, not a computation — no value is summed, averaged or derived.
  //
  // Each year splits into TWO series: complete months on `y{year}`, partial months on
  // `y{year}Partial`. That split is the whole point of the panel. A month with
  // `complete: false` covers a handful of days, so plotting it on the same line as a full
  // month draws a cliff that describes the calendar, not demand — on the live fixture
  // 2026 runs Mar 2,610 → Apr 2,410 → May 2,290 → Aug 404, and that last point is nine
  // days of August, not an 82% collapse. Every mature property hits this on the current
  // month, every month.
  const { data, partials } = useMemo(() => {
    const byMonth = new Map();
    const partialRows = [];
    for (let m = 1; m <= 12; m += 1) byMonth.set(m, { month: m, label: SHORT_MONTHS[m - 1] });
    items.forEach((row) => {
      const point = byMonth.get(Number(row.month));
      if (!point) return;
      if (row.complete === false) {
        point[`y${row.year}Partial`] = row.clicks;
        partialRows.push(row);
      } else {
        point[`y${row.year}`] = row.clicks;
      }
    });
    return { data: Array.from(byMonth.values()), partials: partialRows };
  }, [items]);

  if (items.length === 0 || years.length === 0) return null;

  const peakMonth = fmtMonthNumber(facts?.summary?.peak_month);

  return (
    <div className="space-y-2">
      <div className="min-h-[240px]" role="img" aria-label={sentence || 'Visits by month, one line per year'}>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94A3B8" />
            <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" width={52} />
            <ThemedChartTooltip formatter={(v) => fmtCount(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {years.map((year, i) => [
              // Complete months. `connectNulls` is deliberately OFF: a month with no row
              // at all is missing, and bridging it with a straight segment invents a
              // trend through data that does not exist.
              <Line
                key={`${year}-complete`}
                type="monotone"
                dataKey={`y${year}`}
                name={String(year)}
                stroke={yearColor(i)}
                strokeWidth={i === 0 ? 2.5 : 2}
                strokeDasharray={i === 0 ? undefined : '5 3'}
                dot={{ r: 3 }}
                connectNulls={false}
              />,
              // Partial months: an unconnected, hollow, dimmed marker. No line, so it
              // cannot read as a continuation of the trend, and it is visually distinct
              // from a complete month at a glance. Kept out of the legend — the caption
              // below names the months and their day counts instead.
              <Line
                key={`${year}-partial`}
                type="monotone"
                dataKey={`y${year}Partial`}
                name={`${year} (partial month)`}
                legendType="none"
                stroke="none"
                strokeWidth={0}
                dot={{ r: 4, fill: 'transparent', stroke: yearColor(i), strokeWidth: 2, strokeDasharray: '2 2', opacity: 0.7 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
              />,
            ])}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="text-xs text-gray-400 dark:text-gray-500 space-y-0.5">
        {/* `peak_month` ranks months by IMPRESSIONS; the panel's own sentence names the
            month with the most VISITS. Those are routinely different months — Verde Ranch
            leads on impressions in June and on visits in May — so a bare "busiest month"
            sitting under a sentence about visits reads as the page contradicting itself.
            Naming the measure is what separates them, and it keeps the ranking the
            backend's: nothing here re-ranks either series. "Showed up in searches" is the
            same phrase the monthly table and the headline chart use for impressions. */}
        {peakMonth && <p>Showed up in searches most often in {peakMonth}.</p>}
        {/* Names the month and its day count. A caption that only says "some months are
            partial" leaves the reader to guess which point is the short one — and the
            short one is precisely the point that looks like a collapse. */}
        {partials.length > 0 && (
          <p>
            {partials
              .map((r) => `${fmtMonthNumber(r.month)} ${r.year} covers ${fmtCount(r.days)} ${Number(r.days) === 1 ? 'day' : 'days'} so far`)
              .join('; ')}
            {' '}— shown as {partials.length === 1 ? 'a hollow marker' : 'hollow markers'},
            not joined to the line, because a part-month cannot be compared with a full one.
          </p>
        )}
        {/* Only describes the dashed series when the chart actually draws one. A property
            in its first year has a single solid line, and naming "earlier years" sends the
            reader looking for something that is not there. */}
        {years.length > 1 && <p>Solid line: most recent year. Dashed lines: earlier years.</p>}
      </div>

      {/* ── Expanded only: the monthly table with day counts (§7) ───────────── */}
      {!collapsed && items.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-800 pt-3">
          <PaginatedTable
            columns={MONTH_COLUMNS}
            rows={items}
            rowKey={(r) => `${r.year}-${r.month}`}
            label="months"
            ariaLabel={sentence}
          />
          <CsvButton panelKey="demand_season" name="When people search for your park" columns={CSV_COLUMNS} rows={items} />
        </div>
      )}
    </div>
  );
};

/**
 * Whether opening this card would change what is on screen (spec v2 §3).
 *
 * Expanding adds the monthly table with its day counts (§7) — the numbers behind the
 * overlay, including which months are partial.
 *
 * @param {object} facts the panel's `facts`
 * @returns {boolean} true when expansion reveals something
 */
SeasonOverlay.hasDepth = (facts) => Array.isArray(facts?.items) && facts.items.length > 0;


export default SeasonOverlay;
